/**
 * The day view's model (sections 11.0 and 11.1): what each block shows, and
 * where. Pure functions of the API's day, so the page only lays them out.
 */
import type { Activity, BillingType, Block, Day, DismissReason, LoggedEntry, TicketState } from "../../api/types";
import { formatClockRange, formatShortDay, formatTimeIn, formatTimeRange, todayIn } from "../../lib/dates";
import { formatDuration } from "../../lib/durations";
import { phoneFromGroupKey } from "../../lib/phones";
import { domainOf, emailFromGroupKey } from "../../lib/emailDomains";
import { possessive } from "../../lib/possessive";
import { LANES, type Lane, laneLabel, laneOf, sourceLabel } from "../../lib/sources";
import { OPEN_COLUMN, STATE_LABELS, type TicketView, canClose, ticketColumn, ticketName, ticketView } from "./tickets";

/** What a block needs from the tech, which decides its column and colors. */
export type BlockKind =
  | "needs_client"
  | "in_progress"
  | "ready"
  | "not_logged"
  | "logging"
  | "logged"
  | "dismissed";

export type ColumnId = "needs" | "ready" | "open" | "closed";

/**
 * The board (v0.9.0's P2b): a logged block sits in Logged - Open or Closed by
 * its ticket's state, as does time logged straight in Autotask. "Logged
 * shouldn't be the final form of a ticket, closed is" (the owner, 2026-10-20);
 * the third column is the owner's own name for it (2026-10-21).
 */
export const COLUMNS: readonly { id: ColumnId; title: string; empty: string }[] = [
  { id: "needs", title: "Needs you", empty: "Nothing needs you." },
  { id: "ready", title: "Ready to log", empty: "Nothing ready to log." },
  { id: "open", title: OPEN_COLUMN, empty: "Nothing logged and still open." },
  { id: "closed", title: "Closed", empty: "Nothing closed yet." },
];

const STATUS_LABELS: Record<BlockKind, string> = {
  needs_client: "Needs client",
  in_progress: "In progress",
  ready: "Ready",
  not_logged: "Didn't log",
  logging: "Logging",
  logged: "Logged",
  dismissed: "Dismissed",
};

const COLUMN_OF_KIND: Record<Exclude<BlockKind, "logged">, ColumnId | null> = {
  needs_client: "needs",
  in_progress: "needs",
  ready: "ready",
  // Still waiting to be logged (the owner, 2026-10-04).
  not_logged: "ready",
  logging: "ready",
  dismissed: null,
};

/**
 * A card's column: a logged one's by its ticket's state (ticketColumn). A
 * logged block whose ticket isn't known (a submission that read no ticket
 * back) sits in Logged - Open, as one whose status isn't known does.
 */
function columnOf(kind: BlockKind, ticket: TicketView | null): ColumnId | null {
  if (kind !== "logged") {
    return COLUMN_OF_KIND[kind];
  }
  return ticket === null ? "open" : ticketColumn(ticket.state);
}

const BILLING_LABELS: Record<BillingType, string> = { managed: "Managed", hourly: "Hourly" };

/** Section 5.4's reasons to dismiss a block, in words. */
export const DISMISS_REASONS: readonly { reason: DismissReason; label: string }[] = [
  { reason: "personal", label: "Personal" },
  { reason: "not_client_work", label: "Not client work" },
  { reason: "duplicate", label: "Duplicate" },
  { reason: "already_logged", label: "Already logged in Autotask" },
  { reason: "other", label: "Other" },
];

export function dismissReasonLabel(reason: string | null): string {
  return DISMISS_REASONS.find((r) => r.reason === reason)?.label ?? "Dismissed";
}

export function kindOf(block: Block): BlockKind {
  switch (block.status) {
    case "dismissed":
      return "dismissed";
    case "submitted":
      return "logged";
    case "submitting":
      return "logging";
    case "open":
      return block.client === null ? "needs_client" : "in_progress";
    case "ready":
      if (block.client === null) {
        return "needs_client";
      }
      // A ready block that was sent can only be one Autotask refused: it went
      // back to Ready (section 10.6), and says so on its card (#254).
      return block.has_submission ? "not_logged" : "ready";
    default:
      // A status added to the API doesn't compile until it's handled here
      // (Idea #306). At run time, an unknown one still shows as in progress.
      block.status satisfies never;
      return "in_progress";
  }
}

export interface BlockView {
  id: string;
  kind: BlockKind;
  column: ColumnId | null;
  /** The client's name; for a "Needs client" block, the number, or "Unknown client". */
  title: string;
  /** The block's client, if it has one. */
  clientId: string | null;
  /** The status as text (section 11.0: never color alone); a logged block's by its ticket's state. */
  statusLabel: string;
  /** The detail panel's label, e.g. "Hourly · Ready". */
  heading: string;
  /** The card's one-line summary, e.g. "Hourly · 3 activities". */
  meta: string;
  /** The ribbon capsule's text: short blocks show only a part of it. */
  capsuleLabel: string;
  seconds: number;
  duration: string;
  /** The tech changed the duration: `seconds` is theirs, not the counted time. */
  overridden: boolean;
  countedSeconds: number;
  startedAt: string;
  endedAt: string;
  isHipaa: boolean;
  lanes: Lane[];
  activities: Activity[];
  overlapsOtherClient: boolean;
  continuesAnother: boolean;
  /**
   * The ticket of the logged block it continues: offered when it's logged
   * (ADR 0079), and only while it takes new work (v0.9.0's P4): never one
   * known closed or being closed. Its state, for "Add to open ticket?".
   */
  continuesTicket: { ticketId: number; number: string | null; state: TicketState } | null;
  /** Still going (its block is open): it can be logged once it's ready. */
  ongoing: boolean;
  /** A logged block's own ticket, and whether it's open or closed (v0.9.0's P2b); null otherwise. */
  ticket: TicketView | null;
  /** The number its calls came from: its "Needs client" group, kept after a client is chosen. */
  number: string | null;
  /** Its "Needs client" group as the API has it, e.g. `phone:+19735550142`: what two blocks without a client merge by. */
  groupKey: string | null;
  /** Not being logged, logged or dismissed, and never sent: its client can still change. */
  canChangeClient: boolean;
  /**
   * Open or ready, and never sent to Autotask: it can be dismissed, its
   * duration changed and its activities moved (section 9.4). A block whose
   * submission failed is retried as it was (ADR 0061).
   */
  canDismiss: boolean;
  /** Why it was dismissed, and the tech's note, if it was. */
  dismissReason: string | null;
  dismissNote: string | null;
  /** The Autotask time entry covering it, if it's dismissed as already logged (section 9.6). */
  coveredByTimeEntry: number | null;
  /**
   * Why it's already logged, naming the covering entry's ticket and time,
   * never Autotask's internal ID (Idea #286); null if it isn't.
   */
  coveredBy: string | null;
  /** Where a dismissed block shows when "Show dismissed" is on: the column it left. */
  dismissedFrom: ColumnId | null;
  /** What the tech typed in its Log form, saved on the block: the form starts from it (ADR 0097). */
  draft: { title: string | null; notes: string | null };
}

function remoteCompany(activities: readonly Activity[]): string | null {
  for (const activity of activities) {
    const company = activity.source === "screenconnect" ? activity.details.company : null;
    if (typeof company === "string" && company.trim() !== "") {
      return company;
    }
  }
  return null;
}

function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** Why Axon Time dismissed a block, never the tech (section 9.6): an entry
 * in Autotask covers it. A block of emails only is covered just on a ticket
 * whose contact they went to, or with none, and says which (ADR 0152). */
function coveredBy(block: Block, activities: Activity[], day: Day): string | null {
  if (block.covered_by_time_entry_id === null) {
    return null;
  }
  const restore = "Restore it if it's separate work.";
  const entry = day.logged_in_autotask.find((e) => e.time_entry_id === block.covered_by_time_entry_id);
  if (!entry) {
    return `Dismissed by Axon Time: already logged in Autotask, an entry there covers it. ${restore}`;
  }
  const when = `${formatDuration(entry.counted_seconds)}, ${formatTimeRange(entry.started_at, entry.ended_at, day.user.timezone)}`;
  const covered = entry.ticket_number
    ? `Dismissed by Axon Time: already logged in Autotask on ticket ${entry.ticket_number} (${when}).`
    : `Dismissed by Axon Time: already logged in Autotask, an entry there covers it (${when}).`;
  let contact = "";
  if (activities.length > 0 && activities.every((a) => a.source === "email")) {
    const emails = activities.length === 1 ? "The email" : "The emails";
    contact = entry.ticket_has_contact ? ` ${emails} went to the ticket's contact.` : " The ticket has no contact.";
  }
  return `${covered}${contact} ${restore}`;
}

function viewOf(block: Block, activities: Activity[], day: Day): BlockView {
  const kind = kindOf(block);
  const ticket = kind === "logged" && block.ticket !== null ? ticketView(block.ticket) : null;
  const statusLabel = ticket === null ? STATUS_LABELS[kind] : STATE_LABELS[ticket.state];
  const billing = block.client?.billing_type ? BILLING_LABELS[block.client.billing_type] : null;
  const billingOrMissing = billing ?? "No billing type";
  // A block keeps its group key after the tech picks its client.
  const number = phoneFromGroupKey(block.group_key);
  // A ScreenConnect session's company as ScreenConnect sent it (ADR 0070).
  const company = remoteCompany(activities);
  // An email's other party, when nobody's client yet (ADR 0128).
  const address = emailFromGroupKey(block.group_key);
  const title = block.client?.name ?? number ?? address ?? company ?? "Unknown client";
  const seconds = block.override_seconds ?? block.counted_seconds;
  const duration = formatDuration(seconds);
  const activityCount = count(activities.length, "activity", "activities");
  const editable = (block.status === "open" || block.status === "ready") && !block.has_submission;

  let meta: string;
  switch (kind) {
    case "needs_client":
      meta = number
        ? "Unknown number · assign a client"
        : company
          ? "Company not mapped · assign a client"
          : "No client · assign one";
      break;
    case "in_progress":
      meta = `In progress · ${billingOrMissing}`;
      break;
    case "ready":
      meta = `${billingOrMissing} · ${activityCount}`;
      break;
    case "not_logged":
      meta = "Didn't log · Try again";
      break;
    case "logging":
      meta = "Logging to Autotask…";
      break;
    case "logged":
      // The ticket it went to, as time logged in Autotask shows it (Idea
      // #288); its chip says whether it's open or closed. Never its ID.
      meta = ticket?.number ? `Ticket ${ticket.number}` : `Logged · ${activityCount}`;
      break;
    case "dismissed":
      meta = `Dismissed · ${dismissReasonLabel(block.dismiss_reason)}`;
      break;
  }

  const lanes: Lane[] = [];
  for (const activity of activities) {
    const lane = laneOf(activity.source);
    if (lane && !lanes.includes(lane)) {
      lanes.push(lane);
    }
  }

  return {
    id: block.id,
    kind,
    column: columnOf(kind, ticket),
    title,
    clientId: block.client?.id ?? null,
    statusLabel,
    heading: kind === "needs_client" || billing === null ? statusLabel : `${billing} · ${statusLabel}`,
    meta,
    // The mockup's "?" marks a block with no client yet.
    capsuleLabel: kind === "needs_client" ? "?" : `${title} · ${duration}`,
    seconds,
    duration,
    overridden: block.override_seconds !== null,
    countedSeconds: block.counted_seconds,
    startedAt: block.started_at,
    endedAt: block.ended_at,
    isHipaa: block.client?.is_hipaa ?? false,
    lanes,
    activities,
    overlapsOtherClient: block.overlaps_other_client,
    continuesAnother: block.continues_block_key !== null,
    continuesTicket: block.continues_ticket
      ? {
          ticketId: block.continues_ticket.ticket_id,
          number: block.continues_ticket.number,
          state: block.continues_ticket.state,
        }
      : null,
    ongoing: block.status === "open",
    ticket,
    number,
    groupKey: block.group_key,
    canChangeClient: editable,
    canDismiss: editable,
    dismissReason: block.dismiss_reason,
    dismissNote: block.dismiss_note,
    coveredByTimeEntry: block.covered_by_time_entry_id,
    coveredBy: coveredBy(block, activities, day),
    dismissedFrom: kind === "dismissed" ? (block.client === null ? "needs" : "ready") : null,
    draft: { title: block.draft.title, notes: block.draft.notes },
  };
}

// Instants compare as times: the same instant can be written with or without
// fractional seconds.
function byStart(a: { started_at: string; id: string }, b: { started_at: string; id: string }): number {
  return Date.parse(a.started_at) - Date.parse(b.started_at) || a.id.localeCompare(b.id);
}

/** Every block on the day, in start order, each with its own activities. */
export function blockViews(day: Day): BlockView[] {
  const byId = new Map(day.activities.map((a) => [a.id, a]));
  return [...day.blocks]
    .sort(byStart)
    .map((block) =>
      viewOf(
        block,
        block.activity_ids
          .map((id) => byId.get(id))
          .filter((a): a is Activity => a !== undefined)
          .sort(byStart),
        day,
      ),
    );
}

/** An activity's own title: its summary, or what it was. */
export function activityTitle(activity: Activity): string {
  if (activity.summary.trim() !== "") {
    return activity.summary;
  }
  if (activity.source === "phone") {
    if (activity.direction === "inbound") {
      return "Inbound call";
    }
    return activity.direction === "outbound" ? "Outbound call" : "Call";
  }
  const lane = laneOf(activity.source);
  return lane ? laneLabel(lane) : activity.source;
}

/** Whether an activity of an in-progress block is the one still going: it has no end yet. */
export function stillGoing(activity: Activity, block: BlockView): boolean {
  return block.ongoing && activity.ended_at === null;
}

/** "ScreenConnect · AV-01": the activity's source, as a card names it, and its title. */
function goingName(activity: Activity): string {
  const lane = laneOf(activity.source);
  const source = lane ? (LANES.find((l) => l.lane === lane)?.name ?? laneLabel(lane)) : sourceLabel(activity.source);
  const title = activityTitle(activity);
  return title === source || (lane !== null && title === laneLabel(lane)) ? source : `${source} · ${title}`;
}

/**
 * Why an in-progress block can't be logged yet, naming what's still going and
 * since when (Discussion #878): it said only "Still in progress". A network
 * session ends once nothing has connected for Settings' network gap, so it
 * says to close what's still connected (ADR 0158, decision 7).
 */
export function inProgressNote(block: BlockView, timeZone: string): string {
  const going = block.activities.filter((activity) => stillGoing(activity, block));
  const [first] = going;
  if (first === undefined) {
    return "Still in progress: it can be logged once it's ready.";
  }
  const more = going.length > 1 ? `, and ${String(going.length - 1)} more` : "";
  const ends = going.length > 1 ? "they end" : "it ends";
  const network = going.some((activity) => activity.source === "network")
    ? " A network session ends a while after its last connection: close the browser tab or app still connected."
    : "";
  return `Still in progress: ${goingName(first)} is still going (since ${formatTimeIn(first.started_at, timeZone)})${more}. It can be logged once ${ends}.${network}`;
}

/** The instant an activity's pill ends: its end, or its counted time from the start. */
export function activityEnd(activity: Activity): string {
  if (activity.ended_at !== null) {
    return activity.ended_at;
  }
  return new Date(Date.parse(activity.started_at) + activity.counted_seconds * 1000).toISOString();
}

/**
 * The cards that have just closed: in another column when last seen on this
 * page, in Closed now. They flash the logged green, which now means closed
 * (v0.9.0's P2b; section 11.0's logged moment): a block logged and closed, a
 * ticket closed from here or found closed in Autotask, and time logged in
 * Autotask whose ticket closed. One logged and left open lands in Logged -
 * Open with no flash.
 */
export function newlyClosed(
  before: ReadonlyMap<string, ColumnId | null>,
  cards: readonly { id: string; column: ColumnId | null }[],
): Set<string> {
  return new Set(
    cards
      .filter((c) => c.column === "closed" && before.has(c.id) && before.get(c.id) !== "closed")
      .map((c) => c.id),
  );
}

type Placed = Pick<BlockView, "id" | "column" | "startedAt"> & { activities: readonly Pick<Activity, "id">[] };

/**
 * The card that takes focus when the selected block leaves the board (#247):
 * the block its activities went to (it merged into that one), or else the
 * next card in its column, the one before it, or the first on the board.
 * Null when no card is left.
 */
export function nextCard(gone: Placed, board: readonly Placed[]): string | null {
  const activities = new Set(gone.activities.map((a) => a.id));
  const took = board.find((b) => b.id !== gone.id && b.activities.some((a) => activities.has(a.id)));
  if (took) {
    return took.id;
  }
  const start = (b: Placed) => Date.parse(b.startedAt);
  const inColumn = (column: ColumnId | null) =>
    board.filter((b) => b.id !== gone.id && b.column === column).sort((a, b) => start(a) - start(b));
  const own = inColumn(gone.column);
  const next =
    own.find((b) => start(b) >= start(gone)) ?? own.at(-1) ?? COLUMNS.flatMap((c) => inColumn(c.id))[0];
  return next?.id ?? null;
}

/** Time logged directly in Autotask (section 9.6): never a block, never logged again. */
export const LOGGED_SOURCE = "autotask_time";

/**
 * The activities the ribbon's lanes draw (section 11.0; ADR 0094), and which
 * of them are faded.
 *
 * - Never an excluded activity, nor time logged in Autotask: one with only
 *   hours has no times, and would stretch the ribbon to midnight (ADR 0057).
 * - While "Show dismissed" is off, a dismissed block's activities leave with
 *   its card and capsule, so they don't stretch the hours either. One
 *   dismissed as already logged keeps them: its time still counts in Captured
 *   (ADR 0057), as the day's stats count it.
 * - A dismissed block's pills are faded whenever they're drawn, like its card.
 *
 * An activity whose block isn't in the day's list is always drawn.
 * An email moved in from an earlier day by its thread (ADR 0131) never is:
 * it isn't this day's time; its block's details say its day.
 */
export function ribbonActivities(
  day: Day,
  showDismissed: boolean,
): { activities: Activity[]; faded: Set<string> } {
  const dismissed = day.blocks.filter((b) => b.status === "dismissed");
  const hidden = new Set(
    dismissed.filter((b) => !showDismissed && b.dismiss_reason !== "already_logged").map((b) => b.id),
  );
  const fadedBlocks = new Set(dismissed.map((b) => b.id));
  const timeZone = day.user.timezone;
  const activities = day.activities.filter(
    (a) =>
      !a.is_excluded &&
      a.source !== LOGGED_SOURCE &&
      (a.block_id === null || !hidden.has(a.block_id)) &&
      todayIn(timeZone, new Date(a.started_at)) === day.day,
  );
  const faded = new Set(
    activities.filter((a) => a.block_id !== null && fadedBlocks.has(a.block_id)).map((a) => a.id),
  );
  return { activities, faded };
}

export interface LoggedEntryView {
  id: string;
  /** Its client here, if its ticket's company is one's: what a block dropped on it takes (v0.9.0's P4). */
  clientId: string | null;
  /**
   * Logged - Open or Closed, by its ticket's state, as a logged block's
   * (the owner's decision 3, 2026-10-21; ADR 0183). An entry with no ticket
   * (time on a project task, say) stays in the third column, where Logged
   * was, with no chip: there's no ticket to be open or closed.
   */
  column: "open" | "closed";
  /** Its ticket and that ticket's state; null for an entry with none. */
  ticket: TicketView | null;
  /** The client's name, or "No client" (e.g. time on a project task). */
  title: string;
  /** e.g. "Logged in Autotask · T20261001.0001". */
  meta: string;
  seconds: number;
  duration: string;
  startedAt: string;
  /** Null: Autotask has only a date and hours for it. */
  endedAt: string | null;
  isHipaa: boolean;
  /** How many of the app's blocks it covers (dismissed as already logged). */
  covers: number;
}

function entryView(entry: LoggedEntry): LoggedEntryView {
  const ticket = entry.ticket === null ? null : ticketView(entry.ticket);
  return {
    id: entry.activity_id,
    clientId: entry.client?.id ?? null,
    column: ticket === null ? "open" : ticketColumn(ticket.state),
    ticket,
    title: entry.client?.name ?? "No client",
    meta: entry.ticket_number ? `Logged in Autotask · ${entry.ticket_number}` : "Logged in Autotask",
    seconds: entry.counted_seconds,
    duration: formatDuration(entry.counted_seconds),
    startedAt: entry.started_at,
    endedAt: entry.ended_at,
    isHipaa: entry.client?.is_hipaa ?? false,
    covers: entry.covers.length,
  };
}

/** The day's time logged directly in Autotask, in start order. */
export function loggedEntryViews(day: Day): LoggedEntryView[] {
  return [...day.logged_in_autotask]
    .sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at) || a.activity_id.localeCompare(b.activity_id))
    .map(entryView);
}

/** Whether two blocks merge: if so, the client the one without a client takes; if not, why. */
export type MergeCheck = { merges: true; takes: string | null } | { merges: false; reason: string };

const NOT_EDITABLE: Partial<Record<BlockKind, string>> = {
  logged: "It's logged: a logged block doesn't change.",
  logging: "It's being logged to Autotask.",
  not_logged: "It was sent to Autotask: try logging it again instead.",
  dismissed: "It's dismissed: restore it first.",
};

/**
 * Whether `block` merges with `target`, by the server's rule (ADRs 0062 and
 * 0118; `backend/app/domain/merging.py`), so the Merge button and drag and
 * drop never disagree with each other or with it:
 * - both still to log (open or ready, never sent);
 * - for the same client; or one without a client, which takes the other's;
 * - never two different clients;
 * - two without a client only from the same number or company (the same
 *   "Needs client" group), or with none.
 */
export function mergeCheck(block: BlockView, target: BlockView): MergeCheck {
  if (target.id === block.id) {
    return { merges: false, reason: "It's the same block." };
  }
  for (const b of [target, block]) {
    if (!b.canDismiss) {
      return { merges: false, reason: NOT_EDITABLE[b.kind] ?? "It can't change now." };
    }
  }
  if (block.clientId !== null && target.clientId !== null) {
    return block.clientId === target.clientId
      ? { merges: true, takes: null }
      : { merges: false, reason: "They're for different clients: change one's client first." };
  }
  if (block.clientId !== null || target.clientId !== null) {
    return { merges: true, takes: block.clientId !== null ? block.title : target.title };
  }
  return block.groupKey === target.groupKey
    ? { merges: true, takes: null }
    : {
        merges: false,
        reason:
          "Neither has a client, and they're not from the same number or company: choose a client for one first.",
      };
}

/**
 * A ticket a block can be dropped on to log it there (v0.9.0's P4): a logged
 * block's, a dashed card's of time entered in Autotask, or (P5) a row of the
 * open-tickets tray. Its client is the one a block without a client takes.
 */
export interface TicketTarget {
  ticketId: number;
  /** As techs know it (T20261002.0018); null when it isn't known. */
  number: string | null;
  state: TicketState;
  /** Its client here; null when its company is no client's. */
  clientId: string | null;
  clientName: string | null;
}

/**
 * Whether a block dropped on a ticket starts its Log form on that ticket, and
 * what the ticket says while the block is over it, either way.
 */
export type TicketCheck = { logs: true; ticket: TicketTarget; hint: string } | { logs: false; hint: string };

/** What a block dropped on another card does: merges with it, or logs to its ticket. */
export type DropCheck = MergeCheck | TicketCheck;

/** The ticket of a logged block or of time entered in Autotask, as a drop target; null without one. */
export function ticketOf(card: BlockView | LoggedEntryView): TicketTarget | null {
  if (card.ticket === null) {
    return null;
  }
  return {
    ticketId: card.ticket.ticketId,
    number: card.ticket.number,
    state: card.ticket.state,
    clientId: card.clientId,
    // A dashed card with no client is titled "No client": no name to take.
    clientName: card.clientId === null ? null : card.title,
  };
}

/**
 * Why a ticket takes no drop, by its state; null when it takes new work (ADR
 * 0181's table), or a closed one, reopened to log to it (P9; ADR 0197).
 */
const TICKET_REFUSALS: Record<TicketState, string | null> = {
  open: null,
  close_failed: null,
  closed: null,
  closing: "Its ticket is being closed",
  reopening: "Its ticket is being reopened",
  unknown: "Its ticket's status is unknown",
};

/**
 * Whether `block` can be dropped on `ticket` (v0.9.0's P4; the owner's
 * decision 1, 2026-10-20: "the same ticket, with its own time entry"):
 * - the ticket takes new work: open, or a close that failed. Not being
 *   closed or reopened, or with its status unknown (ADR 0181). A closed one
 *   is reopened first, from its Log form ("Reopen and log"; P9, ADR 0197);
 * - for the block's own client; a block without a client takes the ticket's;
 * - and the block can be logged: ready, not still in progress.
 *
 * A drop writes nothing to Autotask: it opens the block's Log form on that
 * ticket, and the preview, then the server, check it again.
 */
export function ticketCheck(block: BlockView, ticket: TicketTarget): TicketCheck {
  const refusal = TICKET_REFUSALS[ticket.state];
  if (refusal !== null) {
    return { logs: false, hint: refusal };
  }
  if (ticket.clientId === null && block.clientId === null) {
    return { logs: false, hint: "Its ticket's company isn't a client here" };
  }
  if (ticket.clientId === null || (block.clientId !== null && block.clientId !== ticket.clientId)) {
    return { logs: false, hint: "Another client's ticket" };
  }
  if (!block.canDismiss) {
    return { logs: false, hint: NOT_EDITABLE[block.kind] ?? "It can't change now." };
  }
  if (block.ongoing) {
    return { logs: false, hint: "Still in progress: it can be logged once it's ready" };
  }
  const hint =
    ticket.state === "closed"
      ? `Reopen ticket ${ticketName(ticket)} and log ${block.duration} to it`
      : `Log ${block.duration} to ticket ${ticketName(ticket)} (open)`;
  return {
    logs: true,
    ticket,
    hint:
      block.clientId === null && ticket.clientName !== null
        ? `${hint}. It becomes ${possessive(ticket.clientName)}.`
        : hint,
  };
}

/**
 * What dropping `block` on `target`'s card does: on a logged block whose
 * ticket is known, log to that ticket (ticketCheck); on any other, merge as
 * before (mergeCheck), so a logged block with no ticket read back still says
 * it's logged.
 */
export function dropCheck(block: BlockView, target: BlockView): DropCheck {
  const ticket = target.kind === "logged" && target.id !== block.id ? ticketOf(target) : null;
  return ticket === null ? mergeCheck(block, target) : ticketCheck(block, ticket);
}

/**
 * The open ticket a block's "Add to open ticket T…?" chip offers (v0.9.0's
 * P4): the ticket of the logged block it continues, while it's open or its
 * close failed, on a block ready to log. Nothing merges by itself: the chip
 * only opens its Log form on that ticket.
 */
export function openTicketToAdd(block: BlockView): NonNullable<BlockView["continuesTicket"]> | null {
  const ticket = block.continuesTicket;
  return block.kind === "ready" && ticket !== null && canClose(ticket.state) ? ticket : null;
}

/** What merging does to the block without a client (ADR 0118). */
export function takesNote(client: string): string {
  return `Both become ${possessive(client)}.`;
}

/** The day's blocks this one can merge with (`mergeCheck`). */
export function mergeable(block: BlockView, targets: readonly BlockView[]): BlockView[] {
  return targets.filter((t) => mergeCheck(block, t).merges);
}

/** Where a block can split: at each activity's start after the first, with how many go each way. */
export function splitPoints(
  block: BlockView,
  timeZone: string,
): { at: string; before: number; after: number }[] {
  const starts = block.activities.map((a) => a.started_at);
  // An email moved in from an earlier day stays with the day's work (ADR 0131):
  // a split is only at the day's own activities, after its first.
  const day = todayIn(timeZone, new Date(block.startedAt));
  const own = starts.filter((s) => todayIn(timeZone, new Date(s)) === day);
  const times = [...new Set(own.map((s) => Date.parse(s)))].sort((a, b) => a - b).slice(1);
  return times.map((time) => ({
    at: new Date(time).toISOString(),
    before: starts.filter((s) => Date.parse(s) < time).length,
    after: starts.filter((s) => Date.parse(s) >= time).length,
  }));
}

/** An email moved in from an earlier day by its thread (ADR 0131): not on its block's own day. */
export function movedIn(activity: Activity, block: BlockView, timeZone: string): boolean {
  return todayIn(timeZone, new Date(activity.started_at)) !== todayIn(timeZone, new Date(block.startedAt));
}

/** An activity's times, with its day first when it moved in from an earlier one (ADR 0131). */
export function activityRange(activity: Activity, block: BlockView, timeZone: string): string {
  const clock = formatClockRange(activity.started_at, activity.ended_at, timeZone);
  return movedIn(activity, block, timeZone)
    ? `${formatShortDay(todayIn(timeZone, new Date(activity.started_at)))} ${clock}`
    : clock;
}

/**
 * A pane of a block popup's right side, in place of its primary action (ADRs
 * 0193 and 0194): what the popup's menus open with "…", and what a card's
 * right-click menu can open the popup on.
 */
export type PopupPane = "client" | "duration" | "move" | "merge" | "split" | "dismiss" | "vendor";

/**
 * Whether a popup started this way (ADR 0194; `BlockDetails`' `PopupStart`)
 * puts focus somewhere of its own: a pane, the Ticket list, the form's first
 * field, or Close ticket's question. Otherwise its heading takes it.
 */
export function startsOwnFocus(start: {
  pane?: PopupPane | undefined;
  ticketListOpen?: boolean | undefined;
  focusForm?: boolean | undefined;
  closeTicket?: boolean | undefined;
}): boolean {
  return (
    start.pane !== undefined || start.ticketListOpen === true || start.focusForm === true || start.closeTicket === true
  );
}

/**
 * In the day view's Needs you column: still open (In progress), or with no
 * client. A Ready block was matched to a client, so it's dismissed, never a
 * vendor's (ADR 0194 decision 6); the API's rule too.
 */
export function needsYou(block: BlockView): boolean {
  return block.kind === "needs_client" || block.kind === "in_progress";
}

/**
 * Who a block was with, as its Vendor… pane lists them (ADR 0194 decision
 * 6): each email's other party's domain (its group key, as ADR 0132 had it),
 * and each call's number, in E.164 (its details, else its key), once each, in
 * the block's order. The API checks each again.
 */
export function vendorParties(block: BlockView): { domains: string[]; numbers: string[] } {
  const domains: string[] = [];
  const numbers: string[] = [];
  for (const activity of block.activities) {
    if (activity.source === "email") {
      const address = emailFromGroupKey(activity.external_key);
      const domain = address === null ? null : domainOf(address);
      if (domain !== null && !domains.includes(domain)) {
        domains.push(domain);
      }
    } else if (activity.source === "phone") {
      const number = activity.details.number;
      const found =
        typeof number === "string" && number !== ""
          ? number
          : activity.external_key?.startsWith("phone:") === true
            ? activity.external_key.slice("phone:".length)
            : "";
      if (found !== "" && !numbers.includes(found)) {
        numbers.push(found);
      }
    }
  }
  return { domains, numbers };
}

/**
 * The panes a block's popup offers (ADRs 0193 and 0194), each only where it
 * can be used: Change client on a block still to log that has a client (one
 * with none has the chooser already); the rest on a block still to log and
 * never sent, Move and Split only with more than one activity (and somewhere
 * to split), Merge only with a block to merge with, and Vendor… only on a
 * Needs you block with someone to remember.
 */
export function popupPanes(block: BlockView, targets: readonly BlockView[], timeZone: string): PopupPane[] {
  if (block.kind === "dismissed") {
    return [];
  }
  const panes: PopupPane[] = [];
  if (block.canChangeClient && block.kind !== "needs_client") {
    panes.push("client");
  }
  if (block.canDismiss) {
    panes.push("duration");
    if (block.activities.length > 1) {
      panes.push("move");
    }
    if (splitPoints(block, timeZone).length > 0) {
      panes.push("split");
    }
    if (mergeable(block, targets).length > 0) {
      panes.push("merge");
    }
    panes.push("dismiss");
    const parties = vendorParties(block);
    if (needsYou(block) && parties.domains.length + parties.numbers.length > 0) {
      panes.push("vendor");
    }
  }
  return panes;
}

/** A block's column, in words, for its summary. */
const SUMMARY_KINDS: Record<BlockKind, string> = {
  needs_client: "Needs client",
  in_progress: "In progress",
  ready: "Ready",
  not_logged: "Didn't log",
  logging: "Logging",
  logged: "Logged",
  dismissed: "Dismissed",
};

/**
 * What Copy summary copies (ADR 0194, the prototype's): "Example Law Partners
 * · Fri, Oct 23 · 10:52–12:10 · 1h 13m · Ready", and a logged block's ticket.
 * A HIPAA client's says no more than its card does.
 */
export function blockSummary(block: BlockView, timeZone: string): string {
  const state =
    block.kind === "logged" && block.ticket !== null
      ? block.ticket.state === "closed"
        ? "Closed"
        : OPEN_COLUMN
      : SUMMARY_KINDS[block.kind];
  const parts = [
    block.title,
    formatShortDay(todayIn(timeZone, new Date(block.startedAt))),
    formatClockRange(block.startedAt, block.endedAt, timeZone),
    block.duration,
    state,
  ];
  if (block.ticket !== null) {
    parts.push(`Ticket ${ticketName(block.ticket)}`);
  }
  return parts.join(" · ");
}

/**
 * The Ready blocks that could be logged to a logged block's open ticket, as
 * a drop on its card would (ADR 0187; ADR 0194's "Log a Ready block here").
 */
export function readyForTicket(logged: BlockView, blocks: readonly BlockView[]): BlockView[] {
  const ticket = ticketOf(logged);
  if (ticket === null) {
    return [];
  }
  return blocks.filter((b) => b.kind === "ready" && b.id !== logged.id && ticketCheck(b, ticket).logs);
}
